// ------------------- Component for the stock charts  ------------------------------
// ----------------------------------------------------------------------------------

// import all the components and hooks we need
import React, { useState, useEffect } from 'react';
import { AreaChart, Area, LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { useChartData } from '../hooks/useStockData';
import { useAvailableTickers } from '../hooks/useAvailableTickers';
import './StockChart.css';

// set the colours for the lines of the chart
const COLOURS = ['#b8922a', '#22C55E', '#e0834a', '#d4a843', '#A78BFA', '#F472B6', '#94a3b8', '#68d391'];

const StockChart = ({ symbol, timeframe, defaultColumn = 'PX_LAST', title = null }) => { // pass the componenet with symbol, time, column to show and title as props

  // states to manage the selected columns and the column picker dropdown
  const [selectedColumns, setSelectedColumns] = useState([defaultColumn]);
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  // states to manage the compare symbols feature
  const [compareSymbols, setCompareSymbols] = useState([]);
  const [showComparePicker, setShowComparePicker] = useState(false);
  const { tickers: allTickers } = useAvailableTickers(); // load all the tickers from the useAvailableTickers hook
  const [tickerSearch, setTickerSearch] = useState('');

  // load the chart data from calling the api service
  const { chartData, availableColumns, loading, error } = useChartData(symbol, timeframe, compareSymbols);

  // reset selected columns when the symbol or default column changes
  useEffect(() => {
    setSelectedColumns([defaultColumn]);
    setCompareSymbols([]);
  }, [symbol, defaultColumn]);

  // function to allow the user what data should be shown on the chart
  const toggleColumn = (col) => { 
    setSelectedColumns(prev => { // toggle the selected column in state
      const isSelected = prev.includes(col); // check if the column is currently selected
      
      if (isSelected && prev.length > 1) { // if this stock is selected and there is more than 1 column currently selected
        return prev.filter(c => c !== col); // remove it
      } else if (!isSelected) {
        return [...prev, col]; // if this stock is not currently selected, add it to the selection
      } else {
        return prev;// Can't remove last column, keep as-is
      }
    });
  };

  // function to add a compare symbol to the chart
  const addCompare = (sym) => {
    const upper = sym.trim().toUpperCase(); // convert the symbol to uppercase and trim whitespace
    // if the symbol is empty, already selected, or the same as the primary symbol, do nothing
    if (!upper || upper === symbol.toUpperCase() || compareSymbols.includes(upper)) return;

    setCompareSymbols(prev => [...prev, upper]); // add the new compare symbol to the list in state
    setTickerSearch(''); // reset the ticker search input after adding a symbol
  };

  // function to remove a compare symbol from the chart if the user deselects it
  const removeCompare = (sym) => {
    setCompareSymbols(prev => prev.filter(s => s !== sym)) 
  };

  // function to filter the list of all tickers based on the user's search input 
  const filteredTickers = allTickers.filter(t =>

    t.symbol !== symbol.toUpperCase() && // only show tickers that are not the primary symbol and match the search query
    !compareSymbols.includes(t.symbol) &&  // only show tickers that are not already selected as compare symbols
    (t.symbol.toLowerCase().includes(tickerSearch.toLowerCase()) || // filter the tickers based on the search input, case-insensitive
     t.name.toLowerCase().includes(tickerSearch.toLowerCase()))

  ).slice(0, 30); // limit the number of tickers shown in the dropdown to 30 for performance and usability

  // determine if there is one variable or more -> decides if area or line
  const isSingle = selectedColumns.length === 1 && compareSymbols.length === 0;
  const activeCol = selectedColumns[0];

  // build the list of all series to add to the chart -> combines the primary symbol and compare symbols
  const allSeries = [
    ...selectedColumns.map((col) => ({ // create a series for each selected column of the primary symbol
      key: col,
      label: col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()), // format the label to remove the underscore and title case
      color: COLOURS[availableColumns.indexOf(col) % COLOURS.length],
    })),

    ...compareSymbols.flatMap((sym, si) => // for each compare symbol, create the series
      selectedColumns.map((col, ci) => ({
        key: `${sym}_${col}`,
        label: `${sym} ${col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())}`, // same formatting as above, but with the symbol prepended
        color: COLOURS[(selectedColumns.length + si * selectedColumns.length + ci) % COLOURS.length],
      }))
    ),
  ];

  // build the chart title based on the active columna and the symbols
  const chartTitle = title
    || `${activeCol.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} — ${symbol}${compareSymbols.length > 0 ? ` vs ${compareSymbols.join(', ')}` : ''}`;

  const areaColor = availableColumns.length > 0 // set the area colour for the single variable chart
    ? COLOURS[availableColumns.indexOf(activeCol) % COLOURS.length]
    : COLOURS[0];

  if (loading) return ( // if the state is loading, show a loading message instead of the chart
    <div className="stock-chart">
      <h3>{chartTitle}</h3>
      <div style={{ textAlign: 'center', padding: '40px', color: '#9CA3AF' }}>Loading chart data...</div>
    </div>
  );

  if (error) return ( // if there is an error, show an error message instead of the chart and a retry button
  <div className="stock-chart">
    <h3>{chartTitle}</h3>
    <div style={{ textAlign: 'center', padding: '40px', color: '#EF4444' }}>
      Error: {error}
      <div>
        <button onClick={refetch} style={{ marginTop: '10px', padding: '8px 16px', background: '#06B6D4', color: '#0B1220', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>
          Retry
        </button>
      </div>
    </div>
  </div>
);

  return ( // render the chart if there is a success
    <div className="stock-chart">
      <div className="chart-header">
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <h3>{chartTitle}</h3> {/* Display the chart title and source information in the header of the chart */}
          <span className="chart-source">Source: Bloomberg LP</span>
        </div>

        {/* Selectors for the chart */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>

          {/* Compare picker */}
          <div className="column-picker-container">
            <button className="column-picker-btn" onClick={() => setShowComparePicker(!showComparePicker)}> {/* Show the button to the user */}
              Compare {compareSymbols.length > 0 ? `(${compareSymbols.length})` : ''} ▾ 
            </button>

            {showComparePicker && ( // If the user clicks the button, show the dropdown to select compare symbols */}
              <div className="column-picker-dropdown" style={{ minWidth: '260px' }}>
                <div className="column-picker-title">Compare Symbols</div> {/* Title for the compare symbols dropdown */}
                
                {compareSymbols.length > 0 && ( // only show the selected compare symbols if there are any
                  <div style={{ marginBottom: '8px' }}>
                    {compareSymbols.map((sym, i) => ( // loop through all of the selected compare symbols and display them with a remove button
                      <span key={sym} className="compare-tag" style={{ background: COLOURS[(selectedColumns.length + i) % COLOURS.length] }}>
                        {sym}
                        <button onClick={() => removeCompare(sym)}>✕</button>
                      </span>
                    ))}
                  </div>
                )}

                {/* Allow the user to search for tickers */}
                <input 
                  className="compare-search-input"
                  type="text"
                  placeholder="Search ticker..."
                  value={tickerSearch}
                  onChange={e => setTickerSearch(e.target.value)}
                />

                {/* Display filtered tickers */}
                <div style={{ maxHeight: '200px', overflowY: 'auto', marginTop: '6px' }}>
                  {filteredTickers.map(t => ( // loop through the filtered tickers and display them in the dropdown
                    // when the user clicks on a ticker, add it to the compare symbols list and close the dropdown
                    <div key={t.symbol} className="column-picker-item" onClick={() => addCompare(t.symbol)} style={{ cursor: 'pointer' }}> 
                      <span style={{ fontWeight: 600, color: '#E5E7EB' }}>{t.symbol}</span>
                      <span style={{ fontSize: '11px', color: '#9CA3AF', marginLeft: '6px' }}>{t.name}</span>
                    </div>
                  ))}
                  {filteredTickers.length === 0 && ( // if there are no tickers found, show a message to the user
                    <div style={{ fontSize: '11px', color: '#9CA3AF', padding: '8px 4px' }}>No tickers found</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Variable selector */}
          {availableColumns.length > 1 && ( // only show the variable selector if there is more than one variable available
            <div className="column-picker-container">
              {/* show the button to the user to select the variables to plot on the chart*/}
              <button className="column-picker-btn" onClick={() => setShowColumnPicker(!showColumnPicker)}>
                Variables ({selectedColumns.length}) ▾
              </button>
              
              {showColumnPicker && ( // if the user clicks the button, show the dropdown to select variables */}
                <div className="column-picker-dropdown">
                  <div className="column-picker-title">Select Variables to Plot</div>
                  {availableColumns.map((col) => ( // loop through all of the columns and display them in the drop down
                    <label key={col} className="column-picker-item">
                      <input type="checkbox" checked={selectedColumns.includes(col)} onChange={() => toggleColumn(col)} />
                      <span className="column-color-dot" style={{ background: COLOURS[availableColumns.indexOf(col) % COLOURS.length] }} />
                      {/* format the label to remove the underscore and title case */}
                      {col.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} 
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Logic to plot the chart */}
      <ResponsiveContainer width="100%" height="90%">

        {isSingle ? ( // if there is only one variable selected, show an area chart
          <AreaChart data={chartData}>
            <defs>
              <linearGradient id={`grad_${activeCol}`} x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%"  stopColor={areaColor} stopOpacity={0.3} />
                <stop offset="95%" stopColor={areaColor} stopOpacity={0} />
              </linearGradient>
            </defs>

            <CartesianGrid strokeDasharray="3 3" stroke="#1F2933" />
            <XAxis dataKey="time" stroke="#9CA3AF" style={{ fontSize: '11px' }} />
            <YAxis stroke="#9CA3AF" style={{ fontSize: '11px' }} domain={['auto', 'auto']} />
            <Tooltip contentStyle={{ backgroundColor: '#1F2933', border: '1px solid #111827', borderRadius: '4px', color: '#E5E7EB' }} />
            <Area type="monotone" dataKey={activeCol} stroke={areaColor} strokeWidth={2} fillOpacity={1} fill={`url(#grad_${activeCol})`}
              name={activeCol.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())} />
          </AreaChart>

        ) : ( // if there are multiple variables selected, show a line chart
          <LineChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#1F2933" />
            <XAxis dataKey="time" stroke="#9CA3AF" style={{ fontSize: '11px' }} />
            <YAxis stroke="#9CA3AF" style={{ fontSize: '11px' }} domain={['auto', 'auto']} />
            <Tooltip contentStyle={{ backgroundColor: '#1F2933', border: '1px solid #111827', borderRadius: '4px', color: '#E5E7EB' }} />
            <Legend />
            {allSeries.map(({ key, label, color }) => ( // loop through all of the series and display them on the chart with the correct colour and label
              <Line key={key} type="monotone" dataKey={key} stroke={color} strokeWidth={2} dot={false} name={label} connectNulls />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    </div>
  );
};

export default StockChart;
